import axios from 'axios';

// Use same-origin for deployed domains (proxy handles /api routing).
// Fallback to env var only for local dev where no proxy exists.
const ENV_URL = process.env.REACT_APP_BACKEND_URL;
const isLocalDev = typeof window !== 'undefined' && (
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
);
const API_BASE = isLocalDev ? (ENV_URL || window.location.origin) : window.location.origin;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Helper to safely get/set token
// sessionStorage = per-tab (independent sessions), localStorage = fallback for new tabs
const getToken = () => {
  try { const t = sessionStorage.getItem('session_token'); if (t) return t; } catch(e) {}
  try { return localStorage.getItem('session_token'); } catch(e) {}
  return null;
};
const setToken = (token) => {
  try { sessionStorage.setItem('session_token', token); } catch(e) {}
  try { localStorage.setItem('session_token', token); } catch(e) {}
};
const removeToken = () => {
  // Only clear THIS tab's session — other tabs keep their own sessionStorage
  try { sessionStorage.removeItem('session_token'); } catch(e) {}
};

// Attach session token on every request
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors globally - redirect to login on 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      removeToken();
      // Don't redirect on public pages or during OAuth/invite flows
      const path = window.location.pathname;
      const search = window.location.search;
      const isPublicPage = path.includes('/login') || 
                           path.includes('/home') ||
                           path.includes('/reset-password') ||
                           path.includes('/invite') ||
                           search.includes('invite=');
      const isOAuthCallback = window.location.hash.includes('session_id') ||
                              path.includes('/auth/callback') ||
                              search.includes('native_token');
      if (!isPublicPage && !isOAuthCallback) {
        window.location.href = '/home';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authMe = () => api.get('/auth/me');
// GET-based session exchange — resilient to proxy body-stripping, sole method used post-fix
export const authSessionExchange = (sessionId) => api.get(`/auth/session-exchange?session_id=${encodeURIComponent(sessionId)}`);
export const authLogout = () => { removeToken(); return api.post('/auth/logout'); };
export { setToken, removeToken };
export const authOnboard = (data) => api.post('/auth/onboard', data);
export const authGetWorkspaces = () => api.get('/auth/workspaces');
export const authSwitchWorkspace = (workspace_id) => api.post('/auth/switch-workspace', { workspace_id });

// Platform Admin (hidden)
export const platformGetWorkspaces = (params) => api.get('/platform/workspaces', { params });
export const platformGetAnalytics = () => api.get('/platform/analytics');
export const platformMigrate = () => api.post('/platform/migrate-to-workspaces');

// Reference
export const getCountries = () => api.get('/reference/countries');
export const getStates = (country) => api.get('/reference/states', { params: country ? { country } : {} });
export const addState = (name, country) => api.post('/reference/states', { name, country });
export const getDistricts = (state) => api.get('/reference/districts', { params: { state } });
export const getGeoUnits = (type, parent) => api.get('/reference/geo', { params: { type, ...(parent ? { parent } : {}) } });
export const addGeoUnit = (data) => api.post('/reference/geo', data);
export const addDistrict = (name, state) => api.post('/reference/districts', { name, state });

// Products
export const getProducts = (params) => {
  if (typeof params === 'string') params = { company_id: params };
  const p = {};
  if (params?.company_id) p.company_id = params.company_id;
  if (params?.search) p.search = params.search;
  if (params?.page) p.page = params.page;
  if (params?.limit) p.limit = params.limit;
  return api.get('/products', { params: p });
};
export const createProduct = (data, companyId) => api.post('/products', data, { params: companyId ? { company_id: companyId } : {} });
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);

// Aptekas
export const getAptekas = (params) => api.get('/aptekas', { params });
export const createApteka = (data) => api.post('/aptekas', data);
export const updateApteka = (id, data) => api.put(`/aptekas/${id}`, data);
export const toggleSupplyHold = (apteka_id, supply_hold, reason) => api.put(`/aptekas/${apteka_id}/supply-hold`, { supply_hold, reason });

// Credit Permissions
export const getCreditPermissions = () => api.get('/credit-permissions');
export const updateCreditPermissions = (permissions) => api.put('/credit-permissions', permissions);

// Partner Permissions & Assignments
export const getPartnerPermissions = (apteka_id) => api.get(`/aptekas/${apteka_id}/permissions`);
export const updatePartnerPermissions = (apteka_id, permission_overrides) => api.put(`/aptekas/${apteka_id}/permissions`, { permission_overrides });
export const getPartnerActionLogs = (apteka_id, limit = 50) => api.get(`/aptekas/${apteka_id}/action-logs`, { params: { limit } });

// User-Partner Assignments
export const getUserPartnerAssignments = (user_id, apteka_id) => api.get('/user-partner-assignments', { params: { ...(user_id ? { user_id } : {}), ...(apteka_id ? { apteka_id } : {}) } });
export const createUserPartnerAssignment = (user_id, apteka_id, permissions) => api.post('/user-partner-assignments', { user_id, apteka_id, permissions });
export const deleteUserPartnerAssignment = (user_id, apteka_id) => api.delete(`/user-partner-assignments/${user_id}/${apteka_id}`);
export const getUserAssignedPartners = (user_id) => api.get(`/users/${user_id}/assigned-partners`);

// Pricing Rules
export const getPricingRules = (params) => api.get('/pricing-rules', { params });
export const createPricingRule = (data) => api.post('/pricing-rules', data);
export const updatePricingRule = (id, data) => api.put(`/pricing-rules/${id}`, data);
export const deletePricingRule = (id) => api.delete(`/pricing-rules/${id}`);

// Month Status
export const getMonthStatuses = (params) => api.get('/month-status', { params });
export const closeMonth = (month, state, district) => api.post('/month-status/close', { month, state, district });
export const reopenMonth = (month, state, district) => api.post('/month-status/reopen', { month, state, district });

// Apteka Stock
export const getAptekaStock = (aptekaId, month) => api.get('/apteka-stock', { params: { apteka_id: aptekaId, month } });
export const saveAptekaStock = (records, deferRecompute = false) => api.post('/apteka-stock/save', { records, defer_recompute: deferRecompute });

// Payments
export const getPayments = (params) => api.get('/payments', { params });
export const getOpeningDetail = (params) => api.get('/opening-detail', { params });
export const getStatementHistory = (partnerId, params) => api.get(`/partners/${partnerId}/export-history`, { params: { ...params, format: 'json' } });
export const downloadStatementHistory = (partnerId, params) => api.get(`/partners/${partnerId}/export-history`, { params, responseType: 'blob' });
export const downloadDistrictHistory = (params) => api.get('/districts/export-history', { params, responseType: 'blob' });
export const getOrdersGeo = () => api.get('/orders/geo');
export const getOrdersAptekas = () => api.get('/orders/aptekas');
export const getOrdersStock = (state, apteka_id, district) => api.get('/orders/stock', { params: { ...(state ? { state } : {}), ...(apteka_id ? { apteka_id } : {}), ...(district ? { district } : {}) } });
export const getOrdersPendingCount = () => api.get('/orders/pending-count');
export const getOrdersAnalytics = () => api.get('/orders/analytics');
export const getWarehouseOrdersAnalytics = () => api.get('/orders/warehouse-analytics');
export const createMrOrder = (body) => api.post('/orders', body);
export const listMrOrders = (params) => api.get('/orders', { params });
export const patchMrOrder = (id, body) => api.patch(`/orders/${id}`, body);
export const dispatchMrOrder = (id, body) => afterCreate(withDuplicateGuard(() => api.post(`/orders/${id}/dispatch`, body), () => api.post(`/orders/${id}/dispatch`, { ...body, allow_duplicate: true }), (extra) => api.post(`/orders/${id}/dispatch`, { ...body, ...extra })));
export const getOrderTemplates = () => api.get('/order-templates');
export const saveOrderTemplate = (body) => api.post('/order-templates', body);
export const deleteOrderTemplate = (id) => api.delete(`/order-templates/${id}`);
export const getNakladnaya = (orderId) => api.get(`/orders/${orderId}/nakladnaya`, { responseType: 'blob' });
export const getNakladnayaBatch = (date, unprintedOnly) => api.get('/orders/nakladnaya-batch', { params: { date, ...(unprintedOnly ? { unprinted_only: 1 } : {}) }, responseType: 'blob' });
export const getMrAnalytics = (mrId, from, to) => api.get('/orders/mr-analytics', { params: { ...(mrId ? { mr_id: mrId } : {}), ...(from ? { date_from: from } : {}), ...(to ? { date_to: to } : {}) } });
export const getOrderDrivers = () => api.get('/orders/drivers');
export const addOrderDriver = (name) => api.post('/orders/drivers', { name });
export const deleteOrderDriver = (id) => api.delete(`/orders/drivers/${id}`);
export const getNakSettings = () => api.get('/orders/nakladnaya-settings');
export const saveNakSettings = (body) => api.put('/orders/nakladnaya-settings', body);
export const createPayment = (data) => api.post('/payments', data);
export const getPaymentSummary = (aptekaId, month, state) => api.get('/payment-summary', { params: { apteka_id: aptekaId, month, state } });
export const saveAptekaMonthSettings = (data) => api.post('/apteka-month-settings', data);

// Deliveries
export const getDeliveries = (params) => api.get('/deliveries', { params });
const dupMessage = (matches) => {
  const lines = (matches || []).map(m => `• ${m.ref || m.delivery_id} — ${m.date} (month ${m.month}) · ${m.total_units} units · ${Math.round(m.value || 0)} TJS${m.order_ref ? ` · ${m.order_ref}` : ''}`).join('\n');
  return `⚠ DUPLICATE? An identical delivery (same products & quantities) already exists for this partner:\n\n${lines}\n\nDeliver it AGAIN anyway?`;
};
const cancelled = (msg) => { const err = new Error(msg); err.cancelledByUser = true; err.response = { data: { detail: msg } }; return err; };
const withDuplicateGuard = async (call, retry, retryWith) => {
  try { return await call(); }
  catch (e) {
    const d = e?.response?.data?.detail;
    if (e?.response?.status === 409 && d?.code === 'DUPLICATE_DELIVERY') {
      if (window.confirm(dupMessage(d.matches))) return retry();
      throw cancelled('Cancelled — duplicate delivery not created');
    }
    if (e?.response?.status === 409 && d?.code === 'INSUFFICIENT_WAREHOUSE_STOCK' && retryWith) {
      const alts = d.alternatives || [];
      const short = (d.shortages || []).map(x => `• ${x.name}: available ${x.available}, requested ${x.requested}`).join('\n');
      if (alts.length) {
        const pick = window.prompt(`⛔ Not enough stock in ${d.warehouse_name}${d.auto_picked ? ' (auto-picked default)' : ''}:\n${short}\n\nWarehouses that have enough:\n${alts.map((w, i) => `${i + 1}. ${w.name}`).join('\n')}\n\nType the number of the warehouse to deliver from, or Cancel to split the delivery:`, '1');
        const idx = parseInt(pick) - 1;
        if (pick !== null && alts[idx]) return retryWith({ warehouse_id: alts[idx].warehouse_id });
        throw cancelled('Cancelled — delivery not saved (insufficient warehouse stock)');
      }
      window.alert(`⛔ Not enough stock in ${d.warehouse_name}:\n${short}\n\nNo other warehouse has enough. Receive stock first or reduce the quantity — the delivery was NOT saved.`);
      throw cancelled('Not saved — insufficient warehouse stock');
    }
    throw e;
  }
};
const MONTHS_RU = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const mRu = (mk) => MONTHS_RU[parseInt((mk || '').split('-')[1], 10) - 1] || mk;
export const notifyMonthRoll = (d) => {
  if (!d?.month_shifted) return;
  import('sonner').then(({ toast }) => toast.warning(
    `🔒 ${mRu(d.requested_month)} is closed → delivery booked into ${mRu(d.booked_month)}`,
    { duration: 30000, description: `${d.delivery?.ref || ''} ${Math.round(d.delivery?.value || 0).toLocaleString()} TJS is NOT lost — it sits in ${mRu(d.booked_month)}. Do NOT enter it again in ${mRu(d.requested_month)}. A banner in Partner Account shows where it went.`, important: true }
  ));
};
const afterCreate = async (p) => { const r = await p; notifyMonthRoll(r?.data); return r; };
export const createDelivery = (data) => afterCreate(withDuplicateGuard(() => api.post('/deliveries', data), () => api.post('/deliveries', { ...data, allow_duplicate: true }), (extra) => api.post('/deliveries', { ...data, ...extra })));
export const getDeliveryDuplicates = (apteka_id) => api.get('/deliveries/duplicates', { params: { apteka_id } });
export const updateDelivery = (deliveryId, data) => api.put(`/deliveries/${deliveryId}`, data);
export const deleteDelivery = (deliveryId) => api.delete(`/deliveries/${deliveryId}`);

// Warehouses
export const getWarehouses = (params) => api.get('/warehouses', { params });
export const getWarehouseReturns = (params) => api.get('/warehouse-returns', { params });
export const syncWarehouseReturns = (month) => api.post('/warehouse-returns/sync', month ? { month } : {});
export const confirmWarehouseReturn = (returnId, warehouseId, condition) => api.post(`/warehouse-returns/${returnId}/confirm`, { warehouse_id: warehouseId, condition: condition || 'good' });
export const cancelWarehouseReturn = (returnId, note) => api.post(`/warehouse-returns/${returnId}/cancel`, { note });
export const createWarehouse = (data) => api.post('/warehouses', data);
export const updateWarehouse = (id, data) => api.put(`/warehouses/${id}`, data);
export const deleteWarehouse = (id) => api.delete(`/warehouses/${id}`);
export const getWarehouseStock = (warehouseId) => api.get(`/warehouse-stock/${warehouseId}`);
export const getAllWarehouseStock = () => api.get('/warehouse-stock-all');
export const addWarehouseStock = (data) => api.post('/warehouse-stock/add', data);
export const deleteWarehouseStock = (stockId) => api.delete(`/warehouse-stock/${stockId}`);
export const returnToWarehouse = (data) => api.post('/warehouse-stock/return', data);
export const getWarehouseTransactions = (params) => api.get('/warehouse-transactions', { params });

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const updateInvoiceStatus = (id, status) => api.put(`/invoices/${id}/status`, { status });
export const updateInvoice = (invoiceId, data) => api.put(`/invoices/${invoiceId}`, data);
export const getInvoiceOutstanding = (aptekaId) => api.get('/invoice-outstanding', { params: { apteka_id: aptekaId } });
export const allocatePayment = (paymentId) => api.post('/payments/allocate', { payment_id: paymentId });

// Dashboard
export const getDashboard = (state, month, district) => api.get('/dashboard', { params: { state, month, ...(district ? { district } : {}) } });
export const exportDashboardCSV = (state, month, district) => api.get('/dashboard/csv', { params: { state, month, ...(district ? { district } : {}) }, responseType: 'blob' });

// Cash Flow Dashboard APIs
export const getCashFlowSummary = (state, district, month) => api.get('/cash-flow-summary', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), ...(month ? { month } : {}) } });
export const getDistrictDues = (state, month) => api.get('/district-dues', { params: { ...(state ? { state } : {}), ...(month ? { month } : {}) } });
export const getPartnerRiskRanking = (state, district, limit = 20, month) => api.get('/partner-risk-ranking', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), limit, ...(month ? { month } : {}) } });
export const getDashboardNotifications = (state, limit = 20) => api.get('/dashboard-notifications', { params: { ...(state ? { state } : {}), limit } });
export const getDailySummaryReport = (state) => api.get('/daily-summary-report', { params: state ? { state } : {} });

// Action Command Center APIs
const mp = (month) => month ? { month } : {};
export const getActionRequired = (state, district, limit = 15, month) => api.get('/action-required', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), limit, ...mp(month) } });
export const getSmartAlerts = (state, limit = 10, month) => api.get('/smart-alerts', { params: { ...(state ? { state } : {}), limit, ...mp(month) } });
export const getCashFlowTrend = (state, month) => api.get('/cash-flow-trend', { params: { ...(state ? { state } : {}), ...mp(month) } });
export const getDeliveryRiskStatus = (state, month) => api.get('/delivery-risk-status', { params: { ...(state ? { state } : {}), ...mp(month) } });

// Stock Intelligence APIs
export const getStockIntelligence = (state, district, month) => api.get('/stock-intelligence', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), ...mp(month) } });
export const getStockActionItems = (state, district, limit = 15, month) => api.get('/stock-action-items', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), limit, ...mp(month) } });

// Control Intelligence APIs
export const getTodayTarget = (state, district, month) => api.get('/today-target', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), ...mp(month) } });
export const getDeliveryControlList = (state, district, month) => api.get('/delivery-control-list', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), ...mp(month) } });
export const getP2pDueAlerts = (state, month) => api.get('/p2p/due-alerts', { params: { ...(state ? { state } : {}), ...mp(month) } });
export const getActionItemsEnhanced = (state, district, limit = 15, month) => api.get('/action-items-enhanced', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), limit, ...mp(month) } });
export const logPartnerAction = (apteka_id, action, note = '') => api.post('/log-partner-action', { apteka_id, action, note });

// Ledger
export const getLedgerMaster = (month, state, district) => api.get('/ledger-master', { params: { month, ...(state ? { state } : {}), ...(district ? { district } : {}) } });
export const getLedger = (aptekaId, month, state) => api.get(`/ledger/${aptekaId}`, { params: { month, ...(state ? { state } : {}) } });

// Compare Months
export const compareMonths = (monthA, monthB, state, district) => api.get('/compare-months', { params: { month_a: monthA, month_b: monthB, ...(state ? { state } : {}), ...(district ? { district } : {}) } });

// CT Initialization
export const getCtTracker = (month, state, district) => api.get('/ct-init/tracker', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const getCtStatus = (aptekaId, month) => api.get(`/ct-init/status/${aptekaId}`, { params: { month } });
export const ctCarryForward = (aptekaId, month) => api.post('/ct-init/carry-forward', { apteka_id: aptekaId, month });
export const ctBulkInitialize = (month, state, district) => api.post('/ct-init/bulk-initialize', { month, state, ...(district && district !== 'all' ? { district } : {}) });
export const ctMarkEdited = (aptekaId, month) => api.post('/ct-init/mark-edited', { apteka_id: aptekaId, month });
export const getCtReminders = (month, state) => api.get('/ct-init/reminders', { params: { month, state } });
export const getCtReminderSettings = () => api.get('/ct-init/reminder-settings');
export const updateCtReminderSettings = (deadlineDay) => api.put('/ct-init/reminder-settings', { deadline_day: deadlineDay });

// CT Variance Alerts
export const getCtVarianceAlerts = (month, state, district) => api.get('/ct-variance/alerts', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const getCtVariancePartner = (aptekaId, month) => api.get(`/ct-variance/partner/${aptekaId}`, { params: { month } });
export const getCtVarianceSettings = () => api.get('/ct-variance/settings');
export const updateCtVarianceSettings = (settings) => api.put('/ct-variance/settings', settings);

// Companies
export const getCompanies = () => api.get('/companies');
export const createCompany = (data) => api.post('/companies', data);
export const updateCompany = (id, data) => api.put(`/companies/${id}`, data);
export const deleteCompany = (id) => api.delete(`/companies/${id}`);

// Company Catalog
export const getCompanyCatalog = (params) => api.get('/company-catalog', { params });
export const createCompanyCatalogEntry = (data) => api.post('/company-catalog', data);
export const updateCompanyCatalogEntry = (id, data) => api.put(`/company-catalog/${id}`, data);
export const deleteCompanyCatalogEntry = (id) => api.delete(`/company-catalog/${id}`);

// Company Invite Codes & Linking
export const generateInviteCode = (companyId, aptekaId) => api.post(`/companies/${companyId}/generate-code`, aptekaId ? { apteka_id: aptekaId } : {});
export const connectWithCode = (code) => api.post('/companies/connect', { code });
export const getCompanyLinks = () => api.get('/company-links');
export const getInviteCodes = (companyId) => api.get(`/companies/${companyId}/invite-codes`);

export const generateShareCode = (companyId) => api.post(`/companies/${companyId}/generate-share-code`);
export const acceptShareCode = (code) => api.post('/companies/accept-share', { code });
export const getShareCodes = (companyId) => api.get(`/companies/${companyId}/share-codes`);
export const getSharedCompanies = () => api.get('/shared-companies');
export const getAptekaDashboard = (month) => api.get('/apteka-dashboard', { params: { month } });

// Users
export const getUsers = () => api.get('/users');
export const createUser = (data) => api.post('/users', data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Notifications
export const getNotifications = (limit = 50) => api.get('/notifications', { params: { limit } });
export const getUnreadCount = () => api.get('/notifications/unread-count');
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`);
export const markAllNotificationsRead = () => api.put('/notifications/read-all');

// Apteka Blocking
export const getConnectedAptekas = (companyId) => api.get(`/companies/${companyId}/connected-aptekas`);
export const blockApteka = (linkId) => api.put(`/company-links/${linkId}/block`);
export const unblockApteka = (linkId) => api.put(`/company-links/${linkId}/unblock`);

// Chat / Messages
export const getConversations = () => api.get('/conversations');
export const createConversation = (companyId, aptekaId) => api.post('/conversations', { company_id: companyId, apteka_id: aptekaId });
export const getConversationMessages = (convId, limit = 100) => api.get(`/conversations/${convId}/messages`, { params: { limit } });
export const sendMessage = (convId, content) => api.post(`/conversations/${convId}/messages`, { content });
export const getMessagesUnreadCount = () => api.get('/messages/unread-count');

// Seed
export const getShareCodeAnalytics = () => api.get('/share-code-analytics');

// Credit Intelligence
export const getCreditIntelligence = (aptekaId, month, state) => api.get(`/credit-intelligence/${aptekaId}`, { params: { month, state } });
export const getPartnerScores = (month, state) => api.get('/partner-scores', { params: { month, state } });
export const getDeliveryControl = (aptekaId, month, state) => api.get(`/delivery-control/${aptekaId}`, { params: { month, state } });
export const checkDeliveryExposure = (data) => api.post('/delivery-check', data);

// Apteka Catalog (own products)
export const getAptekaCatalog = (companyId) => api.get('/apteka-catalog', { params: companyId ? { company_id: companyId } : {} });
export const createAptekaCatalogEntry = (data) => api.post('/apteka-catalog', data);
export const updateAptekaCatalogEntry = (id, data) => api.put(`/apteka-catalog/${id}`, data);
export const deleteAptekaCatalogEntry = (id) => api.delete(`/apteka-catalog/${id}`);

// Month Opening Wizard
export const getWizardStatus = (month, state, district) => api.get('/wizard/month-opening/status', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const completeWizardStep = (data) => api.post('/wizard/month-opening/complete-step', data);
export const getWizardStockSummary = (month, state, district) => api.get('/wizard/month-opening/stock-summary', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const getWizardDeliverySummary = (month, state, district) => api.get('/wizard/month-opening/delivery-summary', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const getMonthlyTargets = (month, state, district) => api.get('/wizard/month-opening/targets', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const saveMonthlyTargets = (data) => api.post('/wizard/month-opening/targets', data);
export const openMonth = (data) => api.post('/wizard/month-opening/open-month', data);

// MR Dashboard
export const getMRList = (state) => api.get('/mr-dashboard/mrs', { params: { ...(state ? { state } : {}) } });
export const getMRDashboardOverview = (month, state) => api.get('/mr-dashboard/overview', { params: { ...(month ? { month } : {}), ...(state ? { state } : {}) } });
export const getMRPartners = (mrId, month, state, district) => api.get(`/mr-dashboard/${mrId}/partners`, { params: { ...(month ? { month } : {}), ...(state ? { state } : {}), ...(district ? { district } : {}) } });
export const getMRLeaderboard = (month, state) => api.get('/mr-dashboard/leaderboard', { params: { ...(month ? { month } : {}), ...(state ? { state } : {}) } });
export const getMRAlerts = (month, state) => api.get('/mr-dashboard/alerts', { params: { ...(month ? { month } : {}), ...(state ? { state } : {}) } });
export const assignMR = (data) => api.post('/mr-dashboard/assign', data);
export const bulkAssignMR = (data) => api.post('/mr-dashboard/bulk-assign', data);
export const getMRAssignmentHistory = (params) => api.get('/mr-dashboard/assignment-history', { params });
export const sendMRWarning = (data) => api.post('/mr-dashboard/actions/warn', data);
export const blockDeliveryAction = (data) => api.post('/mr-dashboard/actions/block-delivery', data);
export const getUnassignedPartners = (state) => api.get('/mr-dashboard/unassigned', { params: { ...(state ? { state } : {}) } });
export const getMRCheckins = (params) => api.get('/mr-dashboard/checkins', { params });
export const createMR = (data) => api.post('/mr-dashboard/create-mr', data);
export const resendMRInvite = (mrId) => api.post(`/mr-dashboard/resend-invite/${mrId}`);
export const deleteMR = (mrId, confirmName) => api.delete(`/mr-dashboard/delete-mr/${mrId}`, { params: { confirm_name: confirmName } });
export const updateMR = (mrId, data) => api.patch(`/mr-dashboard/update-mr/${mrId}`, data);
export const validateInvite = (token) => api.get(`/auth/invite/${token}`);
export const setupInviteAccount = (token, data) => api.post(`/auth/invite/${token}/setup`, data);

// Manager Analytics
export const getTerritoryOverview = (month, state, district) => api.get('/mr-dashboard/manager-analytics/territory-overview', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });
export const getTopPerformers = (month, state, district, metric = 'sales', limit = 10) => api.get('/mr-dashboard/manager-analytics/top-performers', { params: { month, state, metric, limit, ...(district && district !== 'all' ? { district } : {}) } });
export const getMRPerformanceComparison = (month, state, district) => api.get('/mr-dashboard/manager-analytics/mr-performance', { params: { month, state, ...(district && district !== 'all' ? { district } : {}) } });

// Audit Logs
export const getAuditLogs = (params) => api.get('/audit-logs', { params });
export const getAuditLogsSummary = () => api.get('/audit-logs/summary');

// Delivery Override
export const createDeliveryOverride = (data) => api.post('/delivery-override', data);


// Pharmacy Dashboard
export const getPharmacyDashboard = (apteka_id, month) => api.get(`/pharmacy-dashboard/${apteka_id}`, { params: { month } });

// Product Intelligence
export const getProductIntelligence = (params) => api.get('/product-intelligence', { params });

// Reliability Scores
export const getReliabilityScores = (params) => api.get('/reliability-scores', { params });

// Workspace Connections
export const getMyConnections = (params) => api.get('/connections', { params });
export const searchAvailableWorkspaces = (params) => api.get('/connections/available', { params });
export const sendConnectionInvite = (target_workspace_id, message) => api.post('/connections/invite', { target_workspace_id, message });
export const acceptConnection = (connectionId) => api.post(`/connections/${connectionId}/accept`);
export const rejectConnection = (connectionId, reason) => api.post(`/connections/${connectionId}/reject`, { reason });
export const cancelConnection = (connectionId) => api.post(`/connections/${connectionId}/cancel`);
export const disconnectConnection = (connectionId) => api.delete(`/connections/${connectionId}`);

// Subscriptions
export const getSubscriptionPlans = (workspace_type) => api.get('/subscription/plans', { params: { workspace_type } });
export const getMySubscription = () => api.get('/subscription/my-subscription');
export const createCheckout = (plan_id, origin_url) => api.post('/subscription/checkout', { plan_id, origin_url });
export const checkPaymentStatus = (session_id) => api.get(`/subscription/checkout/status/${session_id}`);
export const checkFeature = (feature_key) => api.get(`/subscription/check-feature/${feature_key}`);
export const adminListSubscriptions = () => api.get('/subscription/admin/subscriptions');
export const adminGetWorkspaceFeatures = (workspace_id) => api.get(`/subscription/admin/workspace/${workspace_id}/features`);
export const adminSetWorkspaceFeatures = (workspace_id, enabled, disabled) => api.post(`/subscription/admin/workspace/${workspace_id}/features`, { enabled, disabled });
export const adminSetWorkspacePlan = (workspace_id, plan_id) => api.post(`/subscription/admin/workspace/${workspace_id}/plan`, { plan_id });

export const seedData = () => api.post('/seed');

// ==================== MR Mobile ====================
export const getMRMobileAptekas = (month) => api.get('/mr-mobile/aptekas', { params: { month } });
export const getMRMobileAcct = (aptekaId, month) => api.get(`/mr-mobile/apteka/${aptekaId}/acct`, { params: { month } });
export const getMRMobileDelivery = (aptekaId, month) => api.get(`/mr-mobile/apteka/${aptekaId}/delivery`, { params: { month } });
export const getMRMobileIntel = (aptekaId, month) => api.get(`/mr-mobile/apteka/${aptekaId}/intel`, { params: { month } });
export const mrMobilePayment = (aptekaId, data) => api.post(`/mr-mobile/apteka/${aptekaId}/payment`, data);
export const mrMobileSupplyToggle = (aptekaId, block) => api.post(`/mr-mobile/apteka/${aptekaId}/supply-toggle`, { block });
export const mrMobileSaveStock = (aptekaId, data) => api.post(`/mr-mobile/apteka/${aptekaId}/save-stock`, data);
export const mrMobileCheckin = (aptekaId, data) => api.post(`/mr-mobile/apteka/${aptekaId}/checkin`, data);
export const mrMobileTodayCheckins = () => api.get('/mr-mobile/checkins/today');
export const getMRCalendar = (month) => api.get('/mr-mobile/calendar', { params: { month } });
export const getMRMyPermissions = () => api.get('/mr-dashboard/my-permissions');
export const mrMobileUpdateLocation = (aptekaId, data) => api.put(`/mr-mobile/apteka/${aptekaId}/location`, data);

// ==================== Pharmacy Mode ====================
export const getPharmacyMode = () => api.get('/pharmacy/mode');
export const pharmacyDisconnect = (identifier) => api.post(`/pharmacy/disconnect/${identifier}`);

// ==================== Outstanding Ledger ====================
export const getOutstandingLedger = (aptekaId, params) => api.get(`/outstanding/${aptekaId}`, { params });
export const getOutstandingLedgerExportUrl = (aptekaId, params) => {
  const base = process.env.REACT_APP_BACKEND_URL || '';
  const qs = new URLSearchParams(params).toString();
  return `${base}/api/outstanding/${aptekaId}/export${qs ? '?' + qs : ''}`;
};

// Low Stock Alerts
export const getLowStockAlerts = (state, district, month) => api.get('/low-stock/alerts', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}), ...(month ? { month } : {}) } });
export const getLowStockCount = (state, district) => api.get('/low-stock/count', { params: { ...(state ? { state } : {}), ...(district ? { district } : {}) } });

// AI Insights
export const getAISalesInsights = (month, state, district) => api.get('/sales-insights', { params: { month, ...(state ? { state } : {}), ...(district ? { district } : {}) } });
export const getAIAptekaReport = (aptekaId, month, state) => api.get(`/apteka-report/${aptekaId}`, { params: { month, ...(state ? { state } : {}) } });

// Bulk Statements
export const downloadBulkStatementsPdf = (apteka_ids, month, state) => api.post('/bulk-statements/pdf', { apteka_ids, month, state }, { responseType: 'blob' });

// Autopilot
export const generateAutopilotDrafts = () => api.post('/autopilot/generate');
export const getAutopilotActions = (status) => api.get('/autopilot/actions', { params: { status } });
export const getAutopilotCount = () => api.get('/autopilot/count');
export const approveAutopilotAction = (actionId, body) => api.post(`/autopilot/actions/${actionId}/approve`, body || {});
export const dismissAutopilotAction = (actionId) => api.post(`/autopilot/actions/${actionId}/dismiss`);

// Apteka Growth & Digest (pharmacy money section)
export const getAptekaGrowth = (month, aptekaId) => api.get('/apteka/growth', { params: { ...(month ? { month } : {}), ...(aptekaId ? { apteka_id: aptekaId } : {}) } });
export const getAptekaDigest = (month, aptekaId) => api.get('/apteka/digest', { params: { ...(month ? { month } : {}), ...(aptekaId ? { apteka_id: aptekaId } : {}) } });
export const regenerateDigest = (month, aptekaId) => api.post('/apteka/digest/regenerate', { month, ...(aptekaId ? { apteka_id: aptekaId } : {}) });
export const trackDigestEvent = (month, event, aptekaId) => api.post('/apteka/digest/track', { month, event, ...(aptekaId ? { apteka_id: aptekaId } : {}) });
export const downloadDigestPdf = (month, aptekaId) => api.get('/apteka/digest/pdf', { params: { month, ...(aptekaId ? { apteka_id: aptekaId } : {}) }, responseType: 'blob' });

// Notification Settings
export const getNotificationSettings = () => api.get('/notification-settings');
export const updateNotificationSettings = (settings) => api.post('/notification-settings', settings);

export default api;
